import { useMemo, useRef } from 'react';
import {
  Outlet,
  useParams,
  NavLink,
  useLocation,
  generatePath,
} from 'react-router-dom';
import { useAppSelector } from '@store';
import {
  ButtonSecondary,
  ButtonSize,
  Filters,
  IconArrowLeft,
  IconExpand,
  IconLoader,
  IconTrash,
  InputText,
  PermissionDenied,
  PermissionEnum,
  Popup,
  SchemaFilterType,
  Table,
} from '@ui/src';
import useItems from './hooks/use-items';
import { columnsItems, columnsLabels } from './variables/list';
import formatItems from './helpers/format-items';
import { DictionaryItem } from '../types';

import ROUTES from '../../../routes';

import cn from '../styles.module.css';

let timeout: NodeJS.Timeout;

const DictionaryItems = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    dictionary,
    dictionaryItems,
    dictionaryItemsPagination,
    selectedItems,
    errorsList,
  } = useAppSelector((state) => state.dictionaries);
  const { activePermissions } = useAppSelector((state) => state.root);

  const { dictionaryId } = useParams();
  const { search } = useLocation();

  const {
    isLoading,
    isFetching,
    itemsIsFetching,
    itemsIsLoading,
    searchValue,
    setSearchValue,
    takeCount,
    filters,
    onFilter,
    errorList,
    setPage,
    filtersComponent,
    handleSubmit,
    onRowsSelect,
    deletePopupIsVisible,
    setDeletePopupIsVisible,
    onlyRemoved,
    setOnlyRemoved,
    onDelete,
    deleteInProgress,
    deleteError,
  } = useItems({
    activePermissions,
    scrollRef,
    dictionaryId,
    search,
    dictionaryItems,
    selectedItems,
  });

  const formattedData = useMemo(() => {
    const data = formatItems(dictionaryItems as DictionaryItem[]);

    return data;
  }, [dictionaryItems, filters]);

  let content;
  let globalContent;

  if (isLoading || isFetching) {
    globalContent = (
      <div className={cn.dictionaryItemsLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading && !isFetching) {
    if (!activePermissions?.includes(PermissionEnum.DICTIONARIES_LIST)) {
      globalContent = <PermissionDenied />;
    } else {
      if (
        !activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST)
      ) {
        content = <PermissionDenied />;
      } else {
        content = (
          <>
            <div className={cn.dictionariesTableWrapper}>
              <Table
                columns={columnsItems}
                data={formattedData || []}
                onSort={() => {}}
                sort={{}}
                filters={filters}
                filtersComponent={filtersComponent}
                columnsLabels={columnsLabels}
                onFilterChange={(values) => {
                  const nextFilters: Filters = {
                    ...values,
                    take: {
                      value: [takeCount.toString()],
                      type: SchemaFilterType.STRING,
                    },
                  };

                  onFilter(nextFilters);
                }}
                error={errorsList || errorList}
                isLoading={itemsIsLoading || itemsIsFetching || takeCount === 0}
                storeKey="dictionaries"
                pagination={dictionaryItemsPagination}
                tableBodyClassName={`${
                  isLoading || isFetching ? cn.tableLoading : ''
                } ${dictionaryItems?.find((item) => item.deletedAt) ? cn.morePadding : ''}`}
                handleSubmit={handleSubmit}
                tableFormClassName={cn.externalAccountsTable}
                take={takeCount.toString()}
                setPage={(data) => {
                  setPage({
                    ...data,
                    filters: {
                      ...filters,
                      take: {
                        value: [takeCount.toString()],
                        type: SchemaFilterType.STRING,
                      },
                    },
                  });
                }}
                onRowsSelect={onRowsSelect}
                tableHeadCellClassName={
                  dictionaryItems?.find((item) => item.deletedAt)
                    ? cn.withRemovedValues
                    : ''
                }
                topContent={
                  <div className={cn.dictionaryItemsTableTopContent}>
                    <InputText
                      name="searchValue"
                      value={searchValue}
                      placeholder="Enter Value, Code or Description"
                      labelClassName={cn.searchInputLabel}
                      onChange={(e) => {
                        clearTimeout(timeout);

                        setSearchValue(e.target.value);

                        const nextFilters: Filters = {
                          ...filters,
                          take: {
                            value: [takeCount.toString()],
                            type: SchemaFilterType.STRING,
                          },
                        };

                        if (e.target.value) {
                          nextFilters.search = {
                            value: [e.target.value],
                            type: SchemaFilterType.STRING,
                          };
                        } else if (!e.target.value && nextFilters.search) {
                          delete nextFilters.search;
                        }

                        timeout = setTimeout(() => {
                          onFilter({
                            ...nextFilters,
                          });
                        }, 500);
                      }}
                    />
                    <div className={cn.dictionaryItemsTableControls}>
                      {activePermissions.includes(
                        PermissionEnum.DICTIONARIES_ITEMS_CREATE_ACTION,
                      ) && (
                        <NavLink
                          to={`${generatePath(
                            ROUTES.DICTIONARIES_LIST_ITEMS_CREATE,
                            { dictionaryId: dictionaryId as string },
                          )}${search || ''}`}
                          className={cn.addDictionaryItemButton}
                        >
                          <IconExpand width={18} height={18} />
                          Add Value
                        </NavLink>
                      )}
                      {selectedItems &&
                        selectedItems?.length > 0 &&
                        activePermissions?.includes(
                          PermissionEnum.DICTIONARIES_ITEMS_DELETE_ACTION,
                        ) && (
                          <>
                            {activePermissions.includes(
                              PermissionEnum.DICTIONARIES_ITEMS_CREATE_ACTION,
                            ) && (
                              <div
                                className={
                                  cn.dictionaryItemsTableControlsSeparator
                                }
                              />
                            )}
                            <ButtonSecondary
                              size={ButtonSize.SMALL}
                              className={`button-white ${cn.deleteSelectedValues}`}
                              icon={<IconTrash width={16} height={16} />}
                              text={`Delete ${selectedItems?.length} value${selectedItems?.length > 1 ? 's' : ''}`}
                              onClick={() => setDeletePopupIsVisible(true)}
                            />
                          </>
                        )}
                      <button
                        type="button"
                        className={`${cn.onlyRemovedItems} ${onlyRemoved ? cn.active : ''}`}
                        onClick={() => {
                          setOnlyRemoved(!onlyRemoved);

                          const nextFilters: Filters = {
                            ...filters,
                          };

                          if (onlyRemoved) {
                            delete nextFilters.onlyRemoved;
                          } else {
                            nextFilters.onlyRemoved = {
                              value: ['true'],
                              type: SchemaFilterType.STRING,
                            };
                          }

                          onFilter(nextFilters);
                        }}
                      >
                        Only removed
                      </button>
                    </div>
                  </div>
                }
                transactionTable
                withScroll
                withVerticalScroll
                clickable
              />
            </div>
            <Outlet />
          </>
        );
      }

      globalContent = (
        <div className={cn.dictionariesBottom} ref={scrollRef}>
          <NavLink to={ROUTES.DICTIONARIES_LIST} className={cn.backLink}>
            <IconArrowLeft width={13} height={11} />
            Back
          </NavLink>
          <div className={cn.dictionariesTitle}>{dictionary?.name}</div>
          {content}
          <Popup
            title="Delete selected values?"
            description={`Delete ${selectedItems?.length} value${selectedItems && selectedItems?.length > 1 ? 's' : ''} from ${dictionary?.name}?`}
            onCancelClick={() => setDeletePopupIsVisible(false)}
            onOkClick={onDelete}
            cancelButtonText="Cancel"
            okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
            okButtonDisabled={deleteInProgress}
            isVisible={deletePopupIsVisible}
            parent={document.getElementById('dirty-popup') as HTMLElement}
          >
            <div>
              {deleteError && (
                <div className={cn.deleteDictionaryItemError}>
                  {deleteError}
                </div>
              )}
            </div>
          </Popup>
          <div id="dirty-popup" />
        </div>
      );
    }
  }

  return globalContent;
};

export default DictionaryItems;
